import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, UntypedFormControl} from '@angular/forms';
import {CountySpendingReportService} from '../../../../services/countySpendingReport.service';
import {
    CountySelection,
    CountySpendingReportParameter,
    FundTypeSelection, PagedListOfCountySpendingReportPreview
} from '../../../../services/api.client';
import {FundTypeService} from '../../../../services/fundType.service';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {ITableHeader, TableComponent} from '../../../shared/table/TableComponent';
import {FileDownloadService} from '../../../../services/FileDownloadService';
import {CountySpendingUrls} from '../../../../api/CountySpendingUrls';
import {distinctUntilChanged, map, takeUntil} from 'rxjs/operators';
import {Disposable} from '../../../../utilities/Disposable';

@Component({
    selector: 'app-county-spending',
    templateUrl: './county-spending.component.html',
    styleUrls: ['./county-spending.component.scss'],
    standalone: false
})
export class CountySpendingComponent extends Disposable implements OnInit {

    @ViewChild(TableComponent) table: TableComponent;

    private _formBuilder = inject(FormBuilder);
    private _countySpendingReportService = inject(CountySpendingReportService);
    private _fundTypeService = inject(FundTypeService);
    private _fileDownloadService = inject(FileDownloadService);

    form: FormGroup;
    counties: CountySelection[] = [];
    fundTypes: FundTypeSelection[] = [];

    currentSortName = 'CountyName';
    currentSorting = false;

    limit = 10;
    currentPage = 1;
    maxSize = 1;
    totalCount: number;
    private readonly _countySpending$ = new BehaviorSubject<PagedListOfCountySpendingReportPreview>(null);
    totalCount$: Observable<number> = this._countySpending$.pipe(map(data => data?.totalCount), distinctUntilChanged());

    private defaultAllCounties = 'allCounties';

    selectedFundTypeIds = new UntypedFormControl([]);

    countySpending$: Observable<PagedListOfCountySpendingReportPreview> = this._countySpending$.asObservable();

    tableHeaders: ITableHeader[] = [
        {text: 'County Name', sortKey: 'CountyName'},
        {text: 'Total Pledges In Progress', sortKey: 'PledgesInProgressTotal'},
        {text: 'Total Pledges Paid', sortKey: 'PledgesPaidTotal'},
        {text: 'Total Pledge Dollars', sortKey: 'PledgeDollarsTotal' },
        {text: 'Unprocessed Public Application Fund Requests', sortKey: 'PledgesUnprocessedTotal' },
        {text: 'Total Applications', sortKey: 'PledgesApplicationsTotal' }
    ];


    get countyName(): FormControl {
        return this.form?.get('countyName') as FormControl;
    }

    ngOnInit(): void {
        this.initForm();

        this.totalCount$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(totalCount => this.totalCount = totalCount);

        combineLatest([
            this._countySpendingReportService.getCounties(),
            this._fundTypeService.getFundPledgeTypes()
        ])
            .pipe(takeUntil(this.$destroyed))
            .subscribe(
            ([counties, fundTypes]) => {
                this.counties = counties;
                this.fundTypes = fundTypes;
            });
    }

    sortCounties(options: ITableHeader): void {
        this.currentSortName = options.sortKey;
        this.currentSorting = !options.ascending;
        this.filterCounties();
    }

    setLimit(limit: number): void {// # of rows per page
        this.limit = limit;
        this.filterCounties();
    }

    setPage(page: number): void {
        setTimeout(() => {
            this.currentPage = page; // set current page
            this.filterCounties();
        });
    }

    filterCounties(): void {
        const parameter = this.buildReportParameters();
        const request = this._countySpendingReportService.previewCountySpendingReport(
            parameter,
            this.currentSortName,
            this.currentSorting,
            this.limit,
            (this.currentPage - 1) * this.limit
        );
        this.table.request = request;
        request
            .pipe(takeUntil(this.$destroyed))
            .subscribe({next: (data) => this._countySpending$.next(data), error: () => {}});
    }

    clearFilters(): void {
        this.selectedFundTypeIds.setValue([]);
        this.countyName.setValue(this.defaultAllCounties);
        this.filterCounties();
    }

    private buildReportParameters(): CountySpendingReportParameter {
        const fundTypeIds = this.selectedFundTypeIds.value;
        const isAllFundTypes = !fundTypeIds || fundTypeIds.length === 0;

        return CountySpendingReportParameter.fromJS({
            fundTypeIds: isAllFundTypes ? null : fundTypeIds,
            isAllFundAdministrativeAgency: isAllFundTypes,
            countyName: this.countyName?.value === this.defaultAllCounties ? null : this.countyName?.value,
            isAllCounties: this.countyName?.value === this.defaultAllCounties
        });
    }
    downloadReport(): string {
        const fundTypeIds = this.selectedFundTypeIds.value;
        const isAllFundTypes = !fundTypeIds || fundTypeIds.length === 0;
        // For download, we need to pass fundTypeIds as a comma-separated string
        const downloadParameters = {
            fundTypeIds: isAllFundTypes ? null : fundTypeIds.join(','),
            isAllFundAdministrativeAgency: isAllFundTypes,
            countyName: this.countyName?.value === this.defaultAllCounties ? null : this.countyName?.value,
            isAllCounties: this.countyName?.value === this.defaultAllCounties
        };

        return this._fileDownloadService.buildUrl(CountySpendingUrls.DOWNLOAD_COUNTY_SPENDING_REPORT, downloadParameters);
    }

    private initForm(): void {
        this.form = this._formBuilder.group({
            countyName: new FormControl<string>(this.defaultAllCounties)
        });
    }
}
